import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Upload, ChevronRight, Clock } from 'lucide-react';
import { grupoDe } from './visitasGrupos';
import { especialidadeDaLinha, dataDoUpload, ehConsolidado, listarUploads, carregarUpload } from './visitasConsolidado';


// Mesma paleta/estratégia de cor por nome usada no DashboardVisitas.js, duplicada aqui
// para que a cor de cada especialidade seja igual nos dois lugares (arquivos autônomos).
const PALETTE = [
  '#0F766E', '#2563EB', '#9333EA', '#DB2777', '#D4351C', '#B5790B',
  '#15803D', '#0891B2', '#7C3AED', '#BE123C', '#4D7C0F', '#A16207',
  '#0E7490', '#9D174D', '#1D4ED8'
];
const specColorCache = {};
function specColor(name) {
  if (specColorCache[name] !== undefined) return specColorCache[name];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  const c = PALETTE[h % PALETTE.length];
  specColorCache[name] = c;
  return c;
}

// Quantos uploads recentes olhar pra montar a lista de especialidades — como agora
// cada especialidade é subida em arquivo separado, isso cobre alguns dias de uploads
// diários sem precisar buscar o histórico inteiro da coleção.
const RECENT_LOOKBACK = 60;

export default function VisitasEspecialidadeHub({ onSelect }) {
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        // Passo 1: última planilha de cada especialidade REAL (ex: "Fisioterapeuta Bobath").
        // A lista vem sem as linhas; só os uploads escolhidos são baixados completos.
        const lista = await listarUploads(RECENT_LOOKBACK);
        const escolhidos = new Map();
        lista.forEach(meta => {
          const key = ehConsolidado(meta) ? '__all__' : meta.especialidade;
          if (!escolhidos.has(key)) escolhidos.set(key, meta.id);
        });
        const bySubEsp = new Map();
        const completos = await Promise.all([...escolhidos.entries()].map(async ([key, id]) => [key, await carregarUpload(id)]));
        completos.forEach(([key, data]) => { if (data) bySubEsp.set(key, data); });

        // Passo 2: soma as subespecialidades que pertencem ao mesmo grupo (Fisio/Fono)
        const groups = new Map();
        bySubEsp.forEach((data, subKey) => {
          const isConsolidado = subKey === '__all__';
          const displayName = isConsolidado ? 'Consolidado (todas as especialidades)' : grupoDe(subKey);
          const rows = data.data || [];
          const faltas = rows.filter(r => !String(r['Data Realiz.'] || '').trim());
          const raw = data.uploadedAt;
          const uploadedAt = !raw ? null : (typeof raw?.toDate === 'function' ? raw.toDate() : new Date(raw));

          if (!groups.has(displayName)) {
            groups.set(displayName, {
              key: displayName, esp: displayName, isConsolidado,
              faltas: 0, pacientesSet: new Set(), fileNames: [], uploadedAt: null,
            });
          }
          const g = groups.get(displayName);
          g.faltas += faltas.length;
          faltas.forEach(r => g.pacientesSet.add(r['Paciente'] || r['Nome Paciente'] || ''));
          if (data.fileName) g.fileNames.push(data.fileName);
          if (!g.uploadedAt || (uploadedAt && uploadedAt > g.uploadedAt)) g.uploadedAt = uploadedAt;
        });

        // Passo 3: a importação automática do IW vem consolidada (todas as especialidades
        // num upload só). Separa as linhas dela por especialidade e cria um card para
        // cada uma — a menos que exista uma planilha manual MAIS NOVA da mesma especialidade.
        const consolidado = bySubEsp.get('__all__');
        if (consolidado) {
          const dataCons = dataDoUpload(consolidado.uploadedAt);
          const porEsp = new Map();
          (consolidado.data || []).forEach(r => {
            if (String(r['Data Realiz.'] || '').trim()) return;
            const nome = grupoDe(especialidadeDaLinha(r));
            if (!porEsp.has(nome)) porEsp.set(nome, { faltas: 0, pacientes: new Set() });
            const e = porEsp.get(nome);
            e.faltas++;
            e.pacientes.add(r['Paciente'] || r['Nome Paciente'] || '');
          });
          porEsp.forEach((e, nome) => {
            const manual = groups.get(nome);
            if (manual && manual.uploadedAt && dataCons && manual.uploadedAt > dataCons) return;
            groups.set(nome, {
              key: nome, esp: nome, isConsolidado: false, faltas: e.faltas, pacientesSet: e.pacientes,
              fileNames: [consolidado.fileName || 'IW (automático)'], uploadedAt: dataCons,
            });
          });
          // Planilhas manuais mais antigas que o consolidado e sem linha no IW ("Geral") saem do painel.
          groups.forEach((g, nome) => {
            if (!g.isConsolidado && !porEsp.has(nome) && g.uploadedAt && dataCons && g.uploadedAt < dataCons) groups.delete(nome);
          });
        }

        const list = [...groups.values()].map(g => ({
          key: g.key, esp: g.esp, isConsolidado: g.isConsolidado,
          faltas: g.faltas, pacientes: g.pacientesSet.size,
          fileName: g.fileNames.length > 1 ? `${g.fileNames.length} planilhas` : (g.fileNames[0] || ''),
          uploadedAt: g.uploadedAt,
        }));

        list.sort((a, b) => {
          if (a.isConsolidado !== b.isConsolidado) return a.isConsolidado ? -1 : 1;
          return b.faltas - a.faltas;
        });
        setCards(list);
      } catch (e) {
        console.error('Erro ao carregar especialidades de visitas:', e);
      }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '80px 40px', textAlign: 'center', color: '#94a3b8', fontSize: '0.95rem', fontWeight: 600 }}>
        <Clock size={36} style={{ margin: '0 auto 12px', color: '#0F766E' }} />
        Carregando especialidades...
      </div>
    );
  }

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', padding: 30 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 6, flexWrap: 'wrap' }}>
        <div style={{
          width: 40, height: 40, borderRadius: 11, background: 'linear-gradient(150deg, #0F766E, #13948a)',
          display: 'grid', placeItems: 'center', flexShrink: 0, boxShadow: '0 4px 14px rgba(15,118,110,.32)',
        }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ width: 22, height: 22 }}>
            <path d="M3 12h3l2 5 4-12 2 7h4" />
          </svg>
        </div>
        <div>
          <h1 style={{ fontSize: 21, fontWeight: 700, color: '#1e293b', letterSpacing: '-.02em', margin: 0 }}>Painel de Visitas Domiciliares</h1>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>Escolha uma especialidade para ver as visitas em falta</p>
        </div>
        <Link to="/uploads?tab=visitas" style={{ marginLeft: 'auto', textDecoration: 'none' }}>
          <button style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, border: '1px solid #cbd5e1', background: '#fff',
            color: '#334155', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, padding: '8px 13px', borderRadius: 8, cursor: 'pointer',
          }}>
            <Upload size={14} /> Carregar CSV
          </button>
        </Link>
      </div>

      {cards.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <div style={{ fontSize: 54, marginBottom: 16 }}>📋</div>
          <h3 style={{ color: '#1e293b', marginBottom: 8, fontWeight: 700, fontSize: '1.2rem' }}>Nenhum dado de visitas disponível</h3>
          <p style={{ color: '#64748b', marginBottom: 24, fontSize: '0.9rem' }}>Suba a primeira planilha (uma por especialidade) na Central de Uploads.</p>
          <Link to="/uploads?tab=visitas" style={{
            display: 'inline-block', padding: '10px 20px', background: '#0F766E', color: '#fff',
            borderRadius: 8, fontWeight: 700, textDecoration: 'none', fontSize: '0.85rem',
          }}>
            Ir para Central de Uploads
          </Link>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14, marginTop: 24 }}>
          {cards.map(c => {
            const col = c.isConsolidado ? '#334155' : specColor(c.esp);
            return (
              <button key={c.key} onClick={() => onSelect(c.key)} style={{
                textAlign: 'left', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14,
                padding: '18px 20px 16px', cursor: 'pointer', boxShadow: '0 1px 2px rgba(20,33,48,.04)',
                transition: 'all .15s', position: 'relative', overflow: 'hidden', fontFamily: 'inherit',
              }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 30px rgba(20,33,48,.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(20,33,48,.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: col }}></span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: col, flexShrink: 0 }}></span>
                  <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>{c.esp}</span>
                  <ChevronRight size={16} style={{ marginLeft: 'auto', color: '#cbd5e1', flexShrink: 0 }} />
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: col, lineHeight: 1 }}>{c.faltas}</div>
                <div style={{ fontSize: 11.5, color: '#64748b', fontWeight: 600, marginTop: 4, marginBottom: 10 }}>
                  visita{c.faltas !== 1 ? 's' : ''} em falta · {c.pacientes} paciente{c.pacientes !== 1 ? 's' : ''}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.fileName} · {c.uploadedAt?.toLocaleString('pt-BR') || '—'}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
